import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin } from '../store.js';
import { setFilters } from '../router.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  DOT,
  GROUP_NOUNS,
  SWARM_SCALES,
  dotText,
  swarmBackground,
  swarmColumns,
  swarmExportRows,
  swarmGeometry,
  swarmModel,
  swarmSummary,
} from '../lib/swarm.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/** What a row of each dimension is called. Matches the heatmap's corner cell. */
const ROW_NOUNS = {
  company: ['Company', 'company'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
  individual: ['Person', 'people'],
};

const SCALE_LABELS = { focus: 'Focus', fit: 'Fit' };
const SCALE_HINTS = {
  focus: 'Clip the outliers so the bands are readable',
  fit: 'Every dot in the picture, however high',
};

/** The plot's height in the frame's own coordinates. Tall enough for the bands to be bands. */
const HEIGHT = 340;

/**
 * The beeswarm (V-6, PRD 10.3; Phase 42): the capacity heatmap as one dot per row per week, so
 * the reader can see what the percentage hides.
 *
 * Every other reading of this response aggregates, and an aggregate cannot tell "everybody is at
 * 92 per cent" from "half the team is at 60 and half at 130". Those are different weeks needing
 * different decisions. Here the dots are the people, the company's own ratio is drawn over them
 * as a dashed rule, and the distance between the cloud and the rule is the answer.
 *
 * Like the bullet rows, the multiples and the horizon, it fetches nothing: the page hands it the
 * same `GET /forecast/heatmap` response the matrix is drawn from, so a dot and the cell it
 * stands for are the same figure of the same run. Every number, every hue and every dot's place
 * comes out of `lib/swarm.js`; this component formats and positions nothing else.
 *
 * Encoding (PRD 7.4): height is the allocation ratio, the background is the reserved threshold
 * vocabulary, and hue is the group — the person's department at the individual grain, the row
 * itself elsewhere — assigned once from the whole response so that emphasising one group cannot
 * move another group's colour. Colour is never the only channel: the bands say the state, the
 * count over the line is printed above every column, and the table carries all of it.
 */
class BeeswarmChart extends StoreMixin(LitElement) {
  static properties = {
    data: { attribute: false }, // a GET /forecast/heatmap response, or null
    loading: { type: Boolean },
    error: { type: String },
    week: { type: String }, // the week the bullet rows draw; marked here, set by clicking a column
    _scale: { state: true },
    _group: { state: true }, // the group emphasised, or null for all of them
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.data = null;
    this.loading = false;
    this.error = null;
    this.week = null;
    this._scale = 'focus';
    this._group = null;
    this._tableOpen = false;
    this._model_ = null;
    this._modelFor = null;
    this._modelScale = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 8px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
        font-weight: 600;
        cursor: pointer;
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }

      /* ---- the marks ---- */
      .dot {
        stroke: var(--surface);
        stroke-width: 1;
        cursor: pointer;
      }
      /* Emphasis is interaction state, never an encoding: a muted dot is still drawn, still
         counted and still hoverable, because the distribution is the whole point and a swarm
         with dots taken out of it would be a different week. PRD Appendix A's reservation of
         opacity is about what a mark *means*; this says nothing about the person. */
      .dot[data-muted='true'] {
        opacity: 0.22;
      }
      .dot[data-selected='true'] {
        stroke: var(--ink-strong);
        stroke-width: 2;
      }
      .dot:focus-visible {
        outline: 2px solid var(--focus);
      }
      .aggregate {
        stroke: var(--ink-strong);
        stroke-width: 2;
        stroke-dasharray: 4 2;
      }
      .overcount {
        font-size: 10.5px;
        font-weight: 700;
        font-family: var(--font-sans);
        fill: var(--status-overloaded);
      }
      .weekmark {
        fill: none;
        stroke: var(--ink-strong);
        stroke-width: 1;
        stroke-dasharray: 2 2;
      }
      .hit {
        fill: transparent;
        cursor: pointer;
      }

      /* ---- chrome ---- */
      .legends {
        display: grid;
        gap: 8px;
        margin-top: 12px;
      }
      .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 16px;
        padding: 0;
        margin: 0;
        list-style: none;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .legend li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .legend button {
        font: inherit;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: none;
        border: 0;
        padding: 0;
        color: inherit;
        cursor: pointer;
      }
      .legend li[data-muted='true'] {
        opacity: 0.5;
      }
      .legend .sw {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 1px solid var(--border-soft);
        flex: none;
      }
      .legend .sw.band {
        width: 22px;
        border-radius: 2px;
      }
      .legend .n {
        font-variant-numeric: tabular-nums;
        color: var(--ink-faint);
      }
      .note {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .state {
        padding: 26px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      details.table data-table {
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Derived

  /**
   * The model, built once per response and scale choice. A window of forty people is five
   * hundred dots, and the card asks for the model in four places on every render — the marks,
   * the legend, the KPIs and the notes — so it is worth not rebuilding it four times a frame.
   */
  get _model() {
    if (!this.data) return null;
    if (this._modelFor !== this.data || this._modelScale !== this._scale) {
      this._model_ = swarmModel(this.data, { scale: this._scale });
      this._modelFor = this.data;
      this._modelScale = this._scale;
    }
    return this._model_;
  }

  get _nouns() {
    return ROW_NOUNS[this.data?.dimension] ?? ROW_NOUNS.company;
  }

  get _groupNouns() {
    return GROUP_NOUNS[this.data?.dimension] ?? GROUP_NOUNS.company;
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  /** Clicking a column sets the week the bullet rows draw, as the matrix's column heads do. */
  _selectWeek(weekStart) {
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: weekStart === this.week ? null : weekStart },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Clicking a dot sets the entity, so the person under the cursor travels to the other views. */
  _toggleEntity(dot) {
    const { filters } = this.appState;
    const dimension = this.data?.dimension;
    if (!dimension || dimension === 'company') return;
    const already = filters.dimension === dimension && filters.entity === dot.row_key;
    setFilters({ dimension, entity: already ? null : dot.row_key });
  }

  _isSelected(dot) {
    const { filters } = this.appState;
    return filters.dimension === this.data?.dimension && filters.entity === dot.row_key;
  }

  /** Emphasis only: the hues are assigned from the whole response and never move. */
  _toggleGroup(key) {
    this._group = this._group === key ? null : key;
  }

  _isMuted(dot) {
    return this._group !== null && dot.group !== this._group;
  }

  _showDot(event, model, dot) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const state = STATUS_STATES.find((s) => s.key === dot.band);
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [
      { label: 'Allocated', value: exact(dot.allocated_hours) },
      { label: '  High confidence', value: exact(dot.high_confidence_hours) },
      { label: 'Capacity', value: exact(dot.capacity_hours) },
      dot.headroom_hours !== null && dot.headroom_hours < 0
        ? { label: 'Over capacity', value: exact(-dot.headroom_hours) }
        : { label: 'Headroom', value: exact(dot.headroom_hours) },
    ];
    const facts = [
      dotText(dot, model.thresholds),
      state ? `${state.icon} ${state.label}` : null,
      dot.group_label,
      dot.clipped ? 'above the top of the scale — drawn at the ceiling' : null,
    ];
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title: `${dot.label} · week of ${longDate(dot.week_start)}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  /** Hovering the column itself gives the week's figures: the spread the dots are drawn from. */
  _showWeek(event, column) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const s = column.stats;
    const rows = [
      { label: 'Company', value: s.aggregate === null ? '—' : percent(s.aggregate) },
      { label: 'Median', value: s.median === null ? '—' : percent(s.median) },
      {
        label: 'Middle half',
        value: s.p25 === null ? '—' : `${percent(s.p25)} to ${percent(s.p75)}`,
      },
      { label: 'Over the line', value: `${count(s.over)} of ${count(s.dots)}` },
      { label: 'Under the band', value: `${count(s.under)} of ${count(s.dots)}` },
    ];
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title: `Week of ${longDate(column.week_start)}`,
      rows,
      footer: `${count(s.dots)} ${this._nouns[1]} drawn${
        s.missing > 0 ? `, ${count(s.missing)} with no capacity` : ''
      } · click to draw the bullet rows for this week`,
    });
  }

  _hideTooltip() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Marks

  _marks = ({ x, y, plot, svg: s }) => {
    const model = this._model;
    if (!model) return nothing;
    const columns = swarmGeometry(model, { x, y });
    const bands = swarmBackground(model.scale, model.thresholds, y);
    const r = DOT.radius;

    return s`
      <g class="bands">
        ${bands.map(
          (band) => s`<rect class="band" data-band=${band.key} x=${plot.left} width=${plot.width}
                            y=${band.y} height=${band.height}
                            fill=${`var(${band.tint})`}></rect>`,
        )}
      </g>
      <g class="hits">
        ${columns.map(
          (column) => s`<rect class="hit" data-week=${column.week_start}
                              x=${column.x} width=${column.width}
                              y=${plot.top} height=${plot.height}
                              @pointermove=${(e) => this._showWeek(e, column)}
                              @pointerleave=${() => this._hideTooltip()}
                              @click=${() => this._selectWeek(column.week_start)}></rect>`,
        )}
      </g>
      <g class="aggregates">
        ${columns.map((column) =>
          column.aggregate_y === null
            ? nothing
            : s`<line class="aggregate" data-week=${column.week_start}
                      x1=${column.x} x2=${column.x + column.width}
                      y1=${column.aggregate_y} y2=${column.aggregate_y}></line>`,
        )}
      </g>
      <g class="dots">
        ${repeat(
          columns.flatMap((c) => c.dots),
          (dot) => dot.key,
          (dot) => s`<circle
            class="dot"
            data-key=${dot.row_key}
            data-week=${dot.week_start}
            data-group=${dot.group}
            data-band=${dot.band ?? ''}
            data-clipped=${dot.clipped ? 'true' : 'false'}
            data-muted=${this._isMuted(dot) ? 'true' : 'false'}
            data-selected=${this._isSelected(dot) ? 'true' : 'false'}
            cx=${dot.x}
            cy=${dot.y}
            r=${r}
            fill=${`var(${this._groupVar(model, dot.group)})`}
            tabindex="0"
            role="img"
            aria-label=${`${dot.label}, week of ${weekLabel(dot.week_start)}: ${dotText(
              dot,
              model.thresholds,
            )}`}
            @pointermove=${(e) => this._showDot(e, model, dot)}
            @pointerleave=${() => this._hideTooltip()}
            @focus=${(e) => this._showDot(e, model, dot)}
            @blur=${() => this._hideTooltip()}
            @click=${() => this._toggleEntity(dot)}
          ></circle>`,
        )}
      </g>
      <g class="counts">
        ${columns.map((column) =>
          column.stats.over === 0
            ? nothing
            : s`<text class="overcount" data-week=${column.week_start}
                      x=${column.centre} y=${plot.top - 3}
                      text-anchor="middle">${column.stats.over}</text>`,
        )}
      </g>
      ${
        this.week
          ? columns
              .filter((c) => c.week_start === this.week)
              .map(
                (column) => s`<rect class="weekmark" x=${column.x} width=${column.width}
                                    y=${plot.top} height=${plot.height}></rect>`,
              )
          : nothing
      }
    `;
  };

  _groupVar(model, key) {
    return model.groups.find((g) => g.key === key)?.var ?? '--type-other';
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls() {
    return html`
      <div class="controls">
        <div class="segmented" role="group" aria-label="Value scale">
          <span>Scale</span>
          <span class="group">
            ${SWARM_SCALES.map(
              (key) =>
                html`<button
                  type="button"
                  data-scale=${key}
                  title=${SCALE_HINTS[key]}
                  aria-pressed=${this._scale === key ? 'true' : 'false'}
                  @click=${() => (this._scale = key)}
                >
                  ${SCALE_LABELS[key]}
                </button>`,
            )}
          </span>
        </div>
        ${
          this._group
            ? html`<button class="link" type="button" @click=${() => (this._group = null)}>
                Show every ${this._groupNouns[1]}
              </button>`
            : nothing
        }
        ${
          this.week
            ? html`<button class="link" type="button" @click=${() => this._selectWeek(this.week)}>
                Clear the marked week (${weekLabel(this.week)})
              </button>`
            : nothing
        }
      </div>
    `;
  }

  /** The groups, and the vocabulary the background is drawn in. */
  _legend(model) {
    return html`
      <div class="legends">
        <ul
          class="legend"
          aria-label=${`What the colours mean: one per ${this._groupNouns[0].toLowerCase()}`}
        >
          ${model.groups.map(
            (group) =>
              html`<li
                data-group=${group.key}
                data-muted=${this._group !== null && this._group !== group.key ? 'true' : 'false'}
              >
                <button
                  type="button"
                  title=${
                    this._group === group.key
                      ? 'Show every group again'
                      : `Emphasise ${group.label} — the hues never move`
                  }
                  @click=${() => this._toggleGroup(group.key)}
                >
                  <span
                    class="sw"
                    style=${`background: var(${group.var});`}
                    aria-hidden="true"
                  ></span>
                  <span>${group.label}</span>
                </button>
                <span class="n">${count(group.rows)}</span>
              </li>`,
          )}
        </ul>
        <ul class="legend" aria-label="What the background and the rules mean">
          ${STATUS_STATES.map(
            (state) =>
              html`<li>
                <span
                  class="sw band"
                  style=${`background: var(${state.tint});`}
                  aria-hidden="true"
                ></span>
                <span>${state.icon} ${state.label}</span>
              </li>`,
          )}
          <li>
            <svg width="22" height="8" aria-hidden="true">
              <line
                x1="0"
                x2="22"
                y1="4"
                y2="4"
                stroke="var(--ink-strong)"
                stroke-width="2"
                stroke-dasharray="4 2"
              ></line>
            </svg>
            <span>The company's own ratio for the week — the figure every other view shows</span>
          </li>
        </ul>
      </div>
    `;
  }

  _kpis(model) {
    const s = swarmSummary(model);
    const worst = s.worst_over;
    const widest = s.widest_spread;
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${worst ? count(worst.stats.over) : '—'}</div>
          <div class="l">
            ${
              worst && worst.stats.over > 0
                ? `of ${count(worst.stats.dots)} ${this._nouns[1]} over the line, ${weekLabel(
                    worst.week_start,
                  )}`
                : `no ${this._nouns[1]} over the line in the window`
            }
          </div>
        </div>
        <div class="kpi">
          <div class="n">
            ${
              widest && widest.stats.p25 !== null
                ? `${percent(widest.stats.p25)}–${percent(widest.stats.p75)}`
                : '—'
            }
          </div>
          <div class="l">
            ${
              widest
                ? `the middle half, ${weekLabel(widest.week_start)} — company ${
                    widest.stats.aggregate === null ? '—' : percent(widest.stats.aggregate)
                  }`
                : 'no spread to measure'
            }
          </div>
        </div>
      </div>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const rowsOf =
      this.data.dimension === 'company' ? 'the company' : `${count(model.rows)} ${this._nouns[1]}`;
    return `one dot per ${this._nouns[1].replace(/s$/, '')} per week · ${rowsOf} over ${count(
      model.weeks.length,
    )} weeks · ${SCALE_LABELS[model.scale.mode].toLowerCase()} scale to ${percent(
      model.scale.max,
    )} · pipeline ${this.data.pipeline_mode}`;
  }

  _notes(model) {
    const { filters } = this.appState;
    const s = swarmSummary(model);
    const notes = [];
    notes.push(
      html`<p class="note" data-note="why">
        Every other reading of this run gives you one number a week. A week at
        ${
          model.columns[0].stats.aggregate === null
            ? 'some ratio'
            : percent(model.columns[0].stats.aggregate)
        }
        can be everybody at that ratio or half the team idle and half of it drowning, and those need
        different decisions. The dots are the ${this._nouns[1]}; the dashed rule is the company's
        own ratio, which is a ratio of two sums and not the middle of the cloud.
        ${
          s.misleading > 0
            ? html`In ${count(s.misleading)} of ${count(s.weeks)} weeks it falls outside the middle
              half of its own dots.`
            : nothing
        }
      </p>`,
    );
    notes.push(
      html`<p class="note" data-note="dodge">
        Dots that would overlap are pushed sideways to the nearest free place, never jittered at
        random: the same week of the same run draws the same swarm every time, so a shape that
        changes is data that changed. The number above each column is how many are over
        ${percent(model.thresholds.overloaded_above)} — the count the matrix shows as overloaded
        cells for that week.
      </p>`,
    );
    if (s.clipped > 0)
      notes.push(
        html`<p class="note" data-note="clipped">
          ${count(s.clipped)} ${s.clipped === 1 ? 'dot is' : 'dots are'} above
          ${percent(model.scale.max)} and drawn at the ceiling, because one row several times
          everyone else's would otherwise squash the bands into a stripe. Switch the scale to
          <strong>Fit</strong> for the whole range, or read the figure in the tooltip and the table.
        </p>`,
      );
    if (s.missing > 0)
      notes.push(
        html`<p class="note" data-note="no-capacity">
          ${count(s.missing)} of ${count(model.expected)}
          ${s.missing === 1 ? 'cell has' : 'cells have'} no capacity that week — role-only demand
          with nobody staffed to it — so there is no ratio to place and no dot. Their hours are in
          the matrix, in the bullet rows, and in the company rule above: that is one reason the rule
          can sit where no dot does.
        </p>`,
      );
    if (this.data.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${count(this.data.withheld_count)}
          ${this.data.withheld_count === 1 ? 'person is' : 'people are'} not shown: your access
          covers yourself and your reports at the individual grain. The company rule still counts
          them, so the rule can sit where no dot does.
        </p>`,
      );
    const unassigned = model.columns.some((c) => c.stats.unassigned_hours > 0);
    if (unassigned && this.data.dimension === 'individual')
      notes.push(
        html`<p class="note" data-note="unassigned">
          Some weeks carry role-only hours that belong to nobody, so they are in the company rule
          and in no dot. That is the other reason the rule can sit above the cloud.
        </p>`,
      );
    if (filters.entity && filters.dimension === this.data.dimension) {
      const dot = model.dots.find((d) => d.row_key === filters.entity);
      notes.push(
        html`<p class="note" data-note="entity">
          ${
            dot
              ? `${dot.label}'s dots are ringed;`
              : 'The entity in the filter bar is not a row here;'
          }
          every dot stays in the chart, because a swarm of one is a sparkline and Phase 25 already
          draws those. Click the dot again to clear the filter.
        </p>`,
      );
    }
    if (this.data.dimension !== 'individual')
      notes.push(
        html`<p class="note" data-note="dimension">
          V-6 is built for the crowded dimension: set the dimension to
          <strong>Individual</strong> and every dot is a person, coloured by their department. At
          this dimension a dot is a ${this._nouns[0].toLowerCase()} row and wears its own hue.
        </p>`,
      );
    if (filters.grain === 'day')
      notes.push(
        html`<p class="note" data-note="grain">
          The grain is daily, so the window is the two weeks the matrix draws as weekdays. The dots
          are still weekly; switch the grain to Week for the whole window.
        </p>`,
      );
    notes.push(
      html`<p class="note" data-note="week">
        Click a column to draw the bullet rows beneath for that week; click a dot to filter every
        view to that ${this._nouns[0].toLowerCase()}.
      </p>`,
    );
    return notes;
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    // The specification is shared with the server's CSV (lib/swarm.js).
    return withFormats(swarmColumns({ dimension: this.data?.dimension }), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        week_start: (v) => weekLabel(v),
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
      },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this.error) body = html`<div class="state error">${this.error}</div>`;
    else if (!this.data && this.loading) body = html`<div class="state">Loading the window…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        ${this._controls()}
        <chart-frame
          .categories=${model.weeks}
          .max=${model.scale.max}
          .marks=${this._marks}
          .formatValue=${(v) => percent(v)}
          .formatCategory=${(c) => weekLabel(c)}
          .rule=${model.thresholds.caution_above}
          rule-label="Capacity"
          band-padding="0.12"
          value-label="Allocation"
          category-label="Week commencing"
          height=${HEIGHT}
        ></chart-frame>
        ${this._legend(model)} ${this._notes(model)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`One row per ${this._nouns[0].toLowerCase()} and week — ${this._subtitle(
              model,
            )}`}
            export-name="beeswarm"
            page-size="25"
            dense
            .columns=${this._columns}
            .rows=${swarmExportRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this.loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Beeswarm</div>
            <h2>Is everyone at the average, or is half the team drowning?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${model ? this._kpis(model) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('beeswarm-chart', BeeswarmChart);
